export type MicroCourseDifficulty = "easy" | "medium" | "hard";
export type MicroCourseAudience = "kid" | "teen" | "adult";

export interface MicroCourse {
  id: string;
  title: string;
  goal: string;
  intro: string;
  content: string;
  example: string;
  exercise: string;
  summary: [string, string, string];
  difficulty: MicroCourseDifficulty;
  audience: MicroCourseAudience;
  duration: number;
}

const biasAdultMicroCourses: MicroCourse[] = [
  {
    id: "bc_mc1",
    title: "Pourquoi ton cerveau cherche a avoir raison",
    goal: "Comprendre l'origine cognitive du biais de confirmation",
    intro: "Notre cerveau aime la coherence et s'appuie sur ce qu'il croit deja.",
    content:
      "Le cerveau economise de l'energie en utilisant des idees deja installees. Il privilegie les informations qui valident ces idees, ce qui renforce le biais de confirmation et filtre les signaux contraires.",
    example:
      "Tu es convaincu qu'un collegue est desorganise. Tu remarques surtout ses oublis et ignores ses dossiers rendus a l'heure.",
    exercise: "Note une croyance, ajoute une preuve pour et une preuve contre en cherchant des sources opposees.",
    summary: [
      "Le cerveau recherche la coherence et rassure nos idees",
      "Il evite les infos qui contredisent pour economiser de l'energie",
      "Questionner ses certitudes ouvre la voie a une analyse plus solide",
    ],
    difficulty: "medium",
    audience: "adult",
    duration: 7,
  },
  {
    id: "bc_mc2",
    title: "Comment savoir si ton jugement est biaise",
    goal: "Identifier les signaux qui montrent qu'un biais est a l'oeuvre",
    intro: "Certains reflexes mentaux indiquent que le biais s'est installe.",
    content:
      "Quand tu recherches uniquement ce qui valide ton opinion, que tu elimines rapidement les objections ou que tu t'entoures d'avis identiques, le biais de confirmation se manifeste. Observer ces signaux aide a reprendre du recul.",
    example:
      "Tu lis trois articles qui defendent ta position et tu t'arretes la, sans verifier des sources critiques.",
    exercise: "Choisis un sujet, liste ce qui ferait changer d'avis. Si la liste est vide, ton biais est probablement actif.",
    summary: [
      "Rechercher uniquement l'approbation est un indicateur de biais",
      "Ignorer rapidement les objections renforce l'angle mort",
      "Nommer les signaux d'alerte aide a corriger la trajectoire",
    ],
    difficulty: "medium",
    audience: "adult",
    duration: 6,
  },
  {
    id: "bc_mc3",
    title: "L'art de chercher la contradiction",
    goal: "Developper le reflexe de confronter ses idees a des avis opposes",
    intro: "Aller vers la contradiction renforce l'esprit critique.",
    content:
      "Chercher volontairement des arguments contraires oblige a verifier la solidite de son raisonnement. Cela peut etre inconfortable mais c'est un accelerateur d'apprentissage et de decisions plus robustes.",
    example:
      "Avant de choisir un produit, tu lis autant d'avis negatifs que positifs pour comprendre les limites reelles.",
    exercise: "Prends une opinion forte, trouve deux sources fiables qui la contestent et resume leur point principal.",
    summary: [
      "La contradiction teste la solidite d'une idee",
      "Elle revele des angles morts invisibles autrement",
      "En faire un reflexe rend les choix plus robustes",
    ],
    difficulty: "medium",
    audience: "adult",
    duration: 7,
  },
  {
    id: "bc_mc4",
    title: "Le biais dans les relations humaines",
    goal: "Repere comment le biais colore nos jugements sur les autres",
    intro: "Nous confirmons nos impressions initiales sur les personnes.",
    content:
      "Une premiere impression influence ce que nous retenons ensuite. Le biais de confirmation pousse a chercher des indices qui valident notre premiere lecture d'une personne, rendant plus difficile le changement de regard.",
    example:
      "Tu penses qu'un ami n'est pas ponctuel et tu remarques surtout ses retards, oubliant ses nombreux rendez-vous a l'heure.",
    exercise: "Choisis quelqu'un de ton entourage. Note un fait qui contredit ton impression initiale et ce que cela change.",
    summary: [
      "Les jugements sociaux sont vite verrouilles par le biais",
      "Chercher des contre-exemples humanise le regard",
      "Mettre a jour ses impressions maintient des relations plus justes",
    ],
    difficulty: "medium",
    audience: "adult",
    duration: 8,
  },
  {
    id: "bc_mc5",
    title: "Les reseaux sociaux : amplificateurs de certitudes",
    goal: "Comprendre comment les algorithmes renforcent le biais",
    intro: "Les fils d'actualite filtrent ce que tu vois et renforcent tes positions.",
    content:
      "Les algorithmes privilegient les contenus que tu apprecies deja. Tu vois plus de publications qui confirment tes idees, ce qui renforce le biais de confirmation et cree une bulle d'informations.",
    example:
      "Apres avoir aime plusieurs articles sur un sujet, ton fil te propose surtout des contenus qui vont dans le meme sens.",
    exercise:
      "Abonne-toi a deux sources qui pensent differemment, lis-les pendant une semaine et note ce que tu decouvres.",
    summary: [
      "Les reseaux amplifient ce que tu valides deja",
      "La bulle d'infos renforce le biais de confirmation",
      "Diversifier ses sources brise cette boucle",
    ],
    difficulty: "medium",
    audience: "adult",
    duration: 7,
  },
];

const biasKidMicroCourses: MicroCourse[] = [
  {
    id: "bc_kid1",
    title: "Pourquoi on ne voit que ce qu'on veut voir",
    goal: "Comprendre que le cerveau aime ce qui lui ressemble",
    intro: "On remarque surtout ce qui confirme nos idees.",
    content:
      "Ton cerveau garde d'abord ce qui lui plait ou ce qu'il connait deja. Il oublie le reste, parfois sans s'en rendre compte.",
    example: "Si tu penses qu'un jeu est nul, tu vois surtout ses defauts et pas ce qui est amusant.",
    exercise: "Choisis un sujet que tu n'aimes pas. Trouve-lui au moins une qualite et ecris-la.",
    summary: [
      "Le cerveau garde ce qui lui convient",
      "Il oublie parfois ce qui pourrait le faire changer d'avis",
      "Chercher un contre-exemple aide a mieux voir",
    ],
    difficulty: "easy",
    audience: "kid",
    duration: 5,
  },
  {
    id: "bc_kid2",
    title: "Chercher les idees differentes",
    goal: "Apprendre a ecouter des avis opposes",
    intro: "Pour progresser, on peut regarder aussi les idees contraires.",
    content:
      "Quand tu n'es pas d'accord, ecouter l'autre peut t'aider a comprendre quelque chose de nouveau. Cela ne veut pas dire que tu changes toujours d'avis.",
    example: "Ton ami prefere un autre sport. En l'ecoutant, tu decouvres pourquoi il aime tant ce sport.",
    exercise: "Demande a quelqu'un son avis contraire au tien et note une chose interessante que tu as apprise.",
    summary: [
      "Les avis differents apportent des idees nouvelles",
      "Ecouter ne veut pas dire abandonner son opinion",
      "Comparer les deux points de vue rend plus curieux",
    ],
    difficulty: "easy",
    audience: "kid",
    duration: 5,
  },
  {
    id: "bc_kid3",
    title: "Les videos qui montrent toujours la meme chose",
    goal: "Comprendre qu'un flux repete peut cacher d'autres idees",
    intro: "Les reseaux montrent souvent ce que tu aimes deja.",
    content:
      "Si tu regardes toujours le meme type de video, les applications t'en montrent encore plus. Tu risques de ne jamais voir d'autres idees ou histoires.",
    example:
      "Si tu regardes surtout des videos d'animaux marrants, tu verras moins de videos scientifiques ou d'autres sujets.",
    exercise: "Choisis un sujet nouveau pour toi et regarde deux videos dessus. Note ce que tu as decouvert.",
    summary: [
      "Les flux repetes renforcent tes gouts",
      "Tu peux manquer d'autres idees importantes",
      "Explorer un nouveau sujet ouvre l'esprit",
    ],
    difficulty: "easy",
    audience: "kid",
    duration: 5,
  },
];

const biasAliases: MicroCourse[] = [
  {
    ...biasAdultMicroCourses[0],
    id: "micro-biais-1",
  },
];

export const demoMicroCourses: MicroCourse[] = [
  ...biasAdultMicroCourses,
  ...biasKidMicroCourses,
  ...biasAliases,
];

export const allMicroCourses: MicroCourse[] = [...demoMicroCourses];
